/**
 * Calendar Utilities
 *
 * Helper functions for managing in-game calendar dates and time calculations.
 */

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                            'July', 'August', 'September', 'October', 'November', 'December'];

export const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Default game date (campaign start)
 */
export const DEFAULT_GAME_DATE = { year: 1, month: 1, day: 1 };

/**
 * Format a game date as "Month Day, Year X"
 * @param {Object} date - Date object with {year, month, day}
 * @returns {string} Formatted date string
 */
export function formatGameDate(date) {
  const monthName = MONTH_NAMES[date.month - 1];
  return `${monthName} ${date.day}, Year ${date.year}`;
}

/**
 * Format a game date with days elapsed
 * @param {Object} date - Date object with {year, month, day}
 * @param {number} daysElapsed - Number of days since campaign start
 * @returns {string} Formatted date string with day count
 */
export function formatGameDateWithDays(date, daysElapsed) {
  return `${formatGameDate(date)} (Day ${daysElapsed})`;
}

/**
 * Calculate days elapsed between two game dates
 * @param {Object} startDate - Start date {year, month, day}
 * @param {Object} currentDate - Current date {year, month, day}
 * @returns {number} Days elapsed (Day 1 = first day)
 */
export function calculateDaysElapsed(startDate, currentDate) {
  let totalStartDays = 0;
  let totalCurrentDays = 0;

  // Add full years
  totalStartDays += (startDate.year - 1) * 365;
  totalCurrentDays += (currentDate.year - 1) * 365;

  // Add full months
  for (let m = 1; m < startDate.month; m++) {
    totalStartDays += MONTH_LENGTHS[m - 1];
  }
  for (let m = 1; m < currentDate.month; m++) {
    totalCurrentDays += MONTH_LENGTHS[m - 1];
  }

  // Add days
  totalStartDays += startDate.day;
  totalCurrentDays += currentDate.day;

  return totalCurrentDays - totalStartDays + 1; // +1 because Day 1 is day 1, not day 0
}

/**
 * Add days to a game date
 * @param {Object} date - Starting date {year, month, day}
 * @param {number} daysToAdd - Number of days to add
 * @returns {Object} New date object {year, month, day}
 */
export function addDaysToDate(date, daysToAdd) {
  let year = date.year;
  let month = date.month;
  let day = date.day + daysToAdd;

  // Handle day overflow
  while (day > MONTH_LENGTHS[month - 1]) {
    day -= MONTH_LENGTHS[month - 1];
    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
  }

  return { year, month, day };
}
